import { Entity, IEntity } from '@lycoris-nubila/ngx-lycoris';

import { AppPrice } from 'application/common/model/app-price';
import { OrganizationPropertyName } from 'organization/model/organization-property-name';
import {
	IOrganizationProperty,
	OrganizationProperty,
} from 'organization/organization-property';

export interface IOrganization extends IEntity {
	readonly name: string;
	readonly sectors: string;
	readonly iconUrl: string;
	readonly logoUrl: string;
	readonly hasPrice: boolean;
	readonly description: string;
	readonly primaryColor: string;
	readonly planPrice: AppPrice;
	readonly shortDescription: string;
	readonly sessionPrice: AppPrice;
	readonly properties: IOrganizationProperty[];

	readonly paymentProviderSubscriptionId: string;

	isPropertyTrue(
		name: OrganizationPropertyName,
		defaultValue?: boolean
	): boolean;

	findPropertyValue(name: OrganizationPropertyName): string;
}

export class Organization extends Entity implements IOrganization {
	constructor(
		id: string,
		name: string,
		sectors: string,
		iconUrl: string,
		logoUrl: string,
		description: string,
		primaryColor: string,
		planPrice: AppPrice,
		updateTimestamp: number,
		shortDescription: string,
		sessionPrice: AppPrice,
		properties: OrganizationProperty[],
		paymentProviderSubscriptionId: string
	) {
		super(id, null, updateTimestamp);
		this._name = name;
		this._sectors = sectors;
		this._iconUrl = iconUrl;
		this._logoUrl = logoUrl;
		this._planPrice = planPrice;
		this._primaryColor = primaryColor;
		this._description = description;
		this._sessionPrice = sessionPrice;
		this._shortDescription = shortDescription;
		this._properties = properties;
		this._paymentProviderSubscriptionId = paymentProviderSubscriptionId;
	}

	private _paymentProviderSubscriptionId: string;

	public get paymentProviderSubscriptionId(): string {
		return this._paymentProviderSubscriptionId;
	}

	private _name: string;

	public get name(): string {
		return this._name;
	}

	private _sectors: string;

	public get sectors(): string {
		return this._sectors;
	}

	private _iconUrl: string;

	public get iconUrl(): string {
		return this._iconUrl;
	}

	private _logoUrl: string;

	public get logoUrl(): string {
		return this._logoUrl;
	}

	private _description: string;

	public get description(): string {
		return this._description;
	}

	private _primaryColor: string;

	public get primaryColor(): string {
		return this._primaryColor;
	}

	private _planPrice: AppPrice;

	public get planPrice(): AppPrice {
		return this._planPrice;
	}

	private _shortDescription: string;

	public get shortDescription(): string {
		return this._shortDescription;
	}

	private _sessionPrice: AppPrice;

	public get sessionPrice(): AppPrice {
		return this._sessionPrice;
	}

	private _properties: OrganizationProperty[];

	public get properties(): OrganizationProperty[] {
		return this._properties;
	}

	public get hasPrice(): boolean {
		return !!this._planPrice?.amount || !!this._sessionPrice?.amount;
	}

	public isPropertyTrue(
		name: OrganizationPropertyName,
		defaultValue = false
	): boolean {
		const property = this._properties.find((p) => p.name === name);

		if (property) {
			return property.value === 'true';
		} else {
			return defaultValue;
		}
	}

	public findPropertyValue(name: OrganizationPropertyName): string {
		return (this._properties || []).find((property) => property.name === name)
			?.value;
	}
}
